import { Component, OnInit, Output, EventEmitter, Injectable, Input, inject } from '@angular/core';
import { ClosetDoorsStandardService } from '../../../../services/configurators-2d/applications/closet-doors-standard.service';
import { CurrentService } from 'src/app/services/current.service';
import { CustomerService } from '../../../../services/customer.service';
import { ActivatedRoute } from '@angular/router';
import { Range } from 'src/app/models/configurators-2d/applications/closet-doors-standard/range';
import { PanelGroup } from 'src/app/models/configurators-2d/applications/closet-doors-standard/panel-group';
import { FrameType } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame-type';
import { ClosetDoorsStandardStep } from './models/closet-doors-standard-step.enum';

@Component({
  selector: 'ng-closet-doors-standard-configuration',
  templateUrl: './closet-doors-standard.component.html',
})
@Injectable()
export class ClosetDoorsStandardComponent implements OnInit {
  /** Expose l'enum dans le template */
  readonly Step = ClosetDoorsStandardStep;

  //Etape de la configuration
  @Input() step: number = ClosetDoorsStandardStep.Environment;

  //Sous-étape de l'étape finitions
  finishesStep = 1;

  @Output() getPreviewData: EventEmitter<any> = new EventEmitter<any>();

  @Output() setGlobalLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() renderToImg: EventEmitter<string> = new EventEmitter<string>();

  @Output() getFinishesStep: EventEmitter<any> = new EventEmitter<boolean>();

  public readonly closetDoorsService = inject(ClosetDoorsStandardService);
  public readonly current = inject(CurrentService);
  public readonly customer = inject(CustomerService);
  private readonly route = inject(ActivatedRoute);

  readonly baseImgURL = this.closetDoorsService.apiUrl;

  ngOnInit() {
    //this.setGlobalLoading.emit(true)

    this.route.params.subscribe(params => {
      //Si un ID de configuration est présent dans l'URL, on récupère cette configuration
      if (params.projectId) {
        let configurationId = this.route.snapshot.params.projectId.replace(this.closetDoorsService.distribId + '_', '');

        //Récupération des emplacements
        this.closetDoorsService.getLocations().then(locations => {
          this.closetDoorsService.emplacements = locations;

          //Récupération des décors (avec configurationId pour inclure les coloris désactivés encore en usage)
          this.closetDoorsService.getDecorations(configurationId).then((decorations: any) => {
            this.closetDoorsService.decorations = decorations;

            //Récupération du projet par l'id
            this.closetDoorsService.getProjectById(configurationId).then((proj: any) => {
              this.current.closetDoors.id = proj.id;
              this.current.closetDoors.title = proj.name;
              this.current.closetDoors.selectedEmplacement = this.closetDoorsService.emplacements.filter(
                (e: any) => e.id === proj.locationId
              )[0];
              this.current.closetDoors.emplacement = this.closetDoorsService.emplacements.filter(
                (e: any) => e.id === proj.locationId
              )[0];
              this.selectHasDrawers(proj.hasDrawer);
              this.selectDrawersLocation(proj.drawerLocations);
              this.current.closetDoors.doorHeight = proj.clientHeight;
              this.current.closetDoors.doorWidth = proj.clientWidth;
              this.current.closetDoors.hasPlinth = proj.clientHasBaseboard;

              if (proj.panelId !== null) {
                // Chargement des groupes de vantaux depuis les dimensions du projet
                this.closetDoorsService.getPanels(proj.clientHeight, proj.clientWidth).then(groups => {
                  this.closetDoorsService.panelGroups = groups;

                  const allPanels = groups.flatMap(g => g.panels);
                  const panel = allPanels.find(p => p.id === proj.panelId);
                  if (panel) {
                    this.current.closetDoors.doorNumber = panel.panelCount;
                    this.current.closetDoors.openingPercentage = panel.percentageOpening;
                  }

                  //Pré-sélection synchrone de la gamme sauvegardée.
                  //On initialise avec l'id seul : isRangeSelected ne compare que l'id,
                  //et le RangeComponent remplacera cet objet par la version complète
                  //lorsque l'utilisateur sélectionnera (ou confirmera) la gamme.
                  if (proj.rangeId != null) {
                    this.current.closetDoors.range = { id: proj.rangeId } as Range;
                  }

                  //Récupération des types de profilé + profilés + structures filtrées
                  this.closetDoorsService.getFrameTypes().then(frameTypes => {
                    this.closetDoorsService.frameTypes = frameTypes;
                    const frameType = proj.frameTypeId
                      ? frameTypes.find((ft: FrameType) => ft.id === proj.frameTypeId) ?? frameTypes[0] ?? null
                      : frameTypes[0] ?? null;
                    return Promise.resolve(frameType);
                  }).then(frameType => {
                    this.closetDoorsService.selectedFrameType = frameType;
                    const savedDoorTypeIds = (proj.panels ?? [])
                      .map((p: any) => p.doorTypeId)
                      .filter((id: number | null): id is number => id != null);
                    return frameType
                      ? this.closetDoorsService.getFrames(frameType.id, this.current.closetDoors.id, savedDoorTypeIds)
                      : Promise.resolve([]);
                  }).then(frameProfiles => {
                    this.closetDoorsService.frameProfiles = frameProfiles;
                    // proj.frameId = Frame.Id (C3/C4/ALU) — pré-sélectionner le bon profilé sauvegardé.
                    // getFrames filtre déjà les profilés incompatibles avec la gamme et les types de porte,
                    // donc le fallback sur [0] est sûr (C3 n'apparaîtra pas pour ATELIER).
                    const savedFrameProfile = proj.frameId ? frameProfiles.find(p => p.id === proj.frameId) : null;
                    this.closetDoorsService.selectedFrameProfile = savedFrameProfile ?? frameProfiles[0] ?? null;
                    return this.closetDoorsService.getStructures(
                      this.current.closetDoors.id,
                      undefined,
                      this.closetDoorsService.selectedFrameProfile?.id
                    );
                  }).then((res: any) => {
                    this.closetDoorsService.profiles = res.frameFinishes;
                    this.closetDoorsService.structures = res.doors;
                    this.closetDoorsService.layers = res.layers;

                    this.current.closetDoors.doors = [];

                    //Construction des portes (sections, calque et structure)
                    for (let i = 0; i < proj.panels.length; i++) {
                      const panelData = proj.panels[i];
                      const structure = this.closetDoorsService.structures.find(
                        (s: any) => panelData.doorTypeId === s.id
                      );
                      const layer = this.closetDoorsService.layers.find(
                        (l: any) => l.frameFinishId === proj.frameFinishId && l.doorTypeId === structure?.id
                      );

                      // Reconstruction des sections à partir des couleurs sauvegardées
                      const sectionColors: { sectionIndex: number; colorId: number }[] =
                        panelData.sectionColors?.length
                          ? panelData.sectionColors
                          // Compat legacy : si sectionColors vide, on utilise colorId sur section 1
                          : panelData.colorId ? [{ sectionIndex: 1, colorId: panelData.colorId }] : [];

                      const sections = (structure?.sections ?? []).map((s: any) => {
                        const saved = sectionColors.find(sc => sc.sectionIndex === s.sectionIndex);
                        const color = s.fixedColorId
                          ? (this.closetDoorsService.decorations?.find((d: any) => d.id === s.fixedColorId) ?? null)
                          : (saved ? (this.closetDoorsService.decorations?.find((d: any) => d.id === saved.colorId) ?? null) : null);
                        return {
                          index: s.sectionIndex,
                          heightPercentage: s.heightPercentage,
                          isFixed: s.isFixed,
                          fixedColorId: s.fixedColorId ?? null,
                          color,
                          availableColorIds: s.availableColorIds ?? [],
                          linkedSectionIndexes: s.linkedSectionIndexes ?? [],
                        };
                      });

                      this.current.closetDoors.doors.push({ id: i + 1, structure, layer, sections });
                    }
                    // proj.frameFinishId = FrameFinish.Id — pré-sélectionner la bonne finition couleur
                    this.current.closetDoors.frameFinish = this.closetDoorsService.profiles.filter(
                      (p: any) => proj.frameFinishId === p.id
                    )[0];

                    this.closetDoorsService.getResume(proj.id).then(
                      res => {
                        this.current.closetDoors.resume = res;
                        this.setGlobalLoading.emit(false);
                      },
                      err => {
                        this.setGlobalLoading.emit(false);
                      }
                    );
                  });
                });
              } else {
                this.setGlobalLoading.emit(false);
              }
            });
          });
        });
      } else {
        //Récupération des environnements
        this.closetDoorsService.getLocations().then(res => {
          this.closetDoorsService.emplacements = res;
        });

        //Récupération des décors
        this.closetDoorsService.getDecorations().then((res: any) => {
          this.closetDoorsService.decorations = res;
        });

        this.setGlobalLoading.emit(false);
      }
    });
  }

  /**
   * Fonction de réception de l'emplacement sélectionné depuis le composant environment
   * @param event objet emplacement
   */
  selectEmplacement(event: any) {
    this.current.closetDoors.selectedEmplacement = event;
  }

  /**
   * Sélection d'une gamme. Reset des données de finitions si la gamme change,
   * puis mise à jour de la preview sans sauvegarder (appel GET avec rangeId).
   * @param range gamme sélectionnée
   */
  selectRange(range: Range): void {
    if (this.current.closetDoors.range?.id !== range.id) {
      // doors = [] déclenche le loader dans la preview et force un rebuild complet
      // dans applyStructuresResult (prevSections vide → sections.color = null).
      this.current.closetDoors.doors = [];
      this.current.closetDoors.frameFinish = null;
      this.current.closetDoors.selectedDoorId = undefined;
      this.current.closetDoors.selectedSectionIndex = undefined;
      this.closetDoorsService.selectedFrameProfile = null;
    }
    this.current.closetDoors.range = range;

    if (this.step === ClosetDoorsStandardStep.Range && this.current.closetDoors.id != null) {
      this.updateRangePreview(range.id);
    }
  }

  /**
   * Met à jour la preview avec les structures de la gamme sélectionnée,
   * sans persister le choix en base (lecture seule côté API).
   * @param rangeId identifiant de la gamme à prévisualiser
   */
  private async updateRangePreview(rangeId: number): Promise<void> {
    const res = await this.closetDoorsService.getStructures(this.current.closetDoors.id, rangeId);
    this.closetDoorsService.applyStructuresResult(res);
  }

  /**
   * Fonction de sélection s'il y a des tiroirs ou non
   * @param event y a t il des tiroirs
   */
  selectHasDrawers(event: boolean): any {
    this.current.closetDoors.hasDrawers = event;
  }

  /**
   * Fonction de sélection de l'emplacement des tiroirs
   * @param event Emplacement des tiroirs
   */
  selectDrawersLocation(event: any): any {
    this.current.closetDoors.drawersLocation = event;
  }

  /**
   * Fonction de récupération de la sous étape de l'étape 3 (finitions profils et decors)
   * @param subStep sous-étape de l'étape 3
   */
  getFinishesSubStep(subStep: number): any {
    this.getFinishesStep.emit(subStep);
  }

  /**
   * Fonction d'activation du bouton suivant
   * @returns booléen d'activation du bouton suivant
   */
  isNextButtonActive(step: number): boolean {
    switch (step) {
      case ClosetDoorsStandardStep.Environment:
        return this.isEnvironmentStepValid();
      case ClosetDoorsStandardStep.DoorConfiguration:
        return this.isDoorConfigurationStepValid();
      case ClosetDoorsStandardStep.Range:
        return this.current.closetDoors.range != null;
      case ClosetDoorsStandardStep.Finishes:
        return this.isDecorationComplete();
      case ClosetDoorsStandardStep.Summary:
        return true;
      default:
        return false;
    }
  }

  /** Vérifie que l'environnement (emplacement + tiroirs) est correctement configuré */
  private isEnvironmentStepValid(): boolean {
    if (this.current.closetDoors.selectedEmplacement === undefined) {
      return false;
    }
    if (
      this.current.closetDoors.hasDrawers &&
      (this.current.closetDoors.drawersLocation === 0 ||
        this.current.closetDoors.drawersLocation === undefined ||
        this.current.closetDoors.drawersLocation === null)
    ) {
      return false;
    }
    return true;
  }

  /** Vérifie que la configuration des vantaux (dimensions, pourcentage, nombre) est valide */
  private isDoorConfigurationStepValid(): boolean {
    const cd = this.current.closetDoors;
    const svc = this.closetDoorsService;

    return (
      cd.doorHeight >= 1200 &&
      cd.doorHeight <= 2600 &&
      cd.doorWidth > 0 &&
      svc.panelGroups?.length > 0 &&
      cd.openingPercentage != null &&
      cd.doorNumber != null &&
      (!cd.needBaseboardCompensator || (cd.baseboardCompensatorQty > 0 && cd.baseboardCompensatorQty <= 2))
    );
  }

  /**
   * Vérifie que toutes les sections configurables de toutes les portes ont une couleur
   * sélectionnée et que cette couleur est toujours disponible au catalogue (non désactivée).
   */
  private isDecorationComplete(): boolean {
    return this.current.closetDoors.doors.every((d: any) =>
      (d.sections ?? [])
        .filter((s: any) => !s.isFixed)
        .every((s: any) => s.color !== null && !s.color.isDisabled)
    );
  }

  isNextButtonHidden(step: number): boolean {
    return step === ClosetDoorsStandardStep.Summary;
  }

  /**
   * Sauvegarde séquentiellement le profil, le type de porte et la couleur de chaque section
   * configurable de chaque vantail.
   */
  async saveDoorsData(): Promise<void> {
    const { id, frameFinish, doors } = this.current.closetDoors;
    // Sauvegarde consolidée et atomique : profilé + types de porte + coloris des sections
    // en une seule requête, pour éviter les doublons dus aux appels concurrents.
    await this.closetDoorsService.saveFinishes(id, frameFinish.id, this.closetDoorsService.buildFinishesPayload(doors));
  }

  /**
   * Fonction de check si la page est accessible ou non
   * @param step étape de la configuration
   * @returns boolean
   */
  checkPageChangeCapability(step: number): boolean {
    switch (step) {
      case ClosetDoorsStandardStep.Environment:
        return true;
      case ClosetDoorsStandardStep.DoorConfiguration:
        return this.isEnvironmentStepValid();
      case ClosetDoorsStandardStep.Range:
        return this.isDoorConfigurationStepValid();
      case ClosetDoorsStandardStep.Finishes:
        return this.current.closetDoors.range != null;
      case ClosetDoorsStandardStep.Summary:
        return this.isDecorationComplete();
      default:
        return false;
    }
  }

  /**
   * Fonction d'initialisation de chaque page
   * @param step étape de la configuration
   * @returns promise
   */
  initPage(step: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      switch (step) {
        case ClosetDoorsStandardStep.Environment:
          resolve(true);
          break;
        case ClosetDoorsStandardStep.DoorConfiguration:
          if (this.current.closetDoors.id === null) {
            this.closetDoorsService
              .createConfigurationByEmplacement(
                this.current.closetDoors.title,
                this.current.closetDoors.selectedEmplacement.id,
                this.current.closetDoors.hasDrawers,
                this.current.closetDoors.drawersLocation
              )
              .then(
                (res: string) => {
                  this.current.closetDoors.id = res;
                  resolve(true);
                },
                err => reject(err)
              );
          } else {
            this.closetDoorsService
              .saveEnvironment(
                this.current.closetDoors?.id,
                this.current.closetDoors.selectedEmplacement.id,
                this.current.closetDoors.hasDrawers,
                this.current.closetDoors.drawersLocation
              )
              .then(
                () => resolve(true),
                err => reject(err)
              );
          }
          break;
        case ClosetDoorsStandardStep.Range:
          // Les dimensions sont sauvegardées ici pour que l'API puisse filtrer les gammes compatibles
          const allPanels = (this.closetDoorsService.panelGroups as PanelGroup[]).flatMap(g => g.panels);
          const panelId = allPanels.find(p => p.panelCount === this.current.closetDoors.doorNumber)?.id;
          this.closetDoorsService
            .saveDimensions(
              this.current.closetDoors.id,
              this.current.closetDoors.doorHeight,
              this.current.closetDoors.doorWidth,
              this.current.closetDoors.hasPlinth,
              this.current.closetDoors.needBaseboardCompensator,
              this.current.closetDoors.baseboardCompensatorQty,
              panelId
            )
            .then(
              () => resolve(true),
              err => reject(err)
            );
          break;
        case ClosetDoorsStandardStep.Finishes:
          this.closetDoorsService
            .saveRange(this.current.closetDoors.id, this.current.closetDoors.range.id)
            .then(() => this.closetDoorsService.getFrameTypes())
            .then(frameTypes => {
              this.closetDoorsService.frameTypes = frameTypes;
              const currentFrameTypeId = this.closetDoorsService.selectedFrameType?.id;
              this.closetDoorsService.selectedFrameType = (currentFrameTypeId
                ? frameTypes.find(ft => ft.id === currentFrameTypeId)
                : null) ?? frameTypes[0] ?? null;
              const currentDoorTypeIds = (this.current.closetDoors.doors as any[])
                .map(d => d.structure?.id)
                .filter((id): id is number => id != null);
              return this.closetDoorsService.selectedFrameType
                ? this.closetDoorsService.getFrames(this.closetDoorsService.selectedFrameType.id, this.current.closetDoors.id, currentDoorTypeIds)
                : Promise.resolve([]);
            })
            .then(frameProfiles => {
              this.closetDoorsService.frameProfiles = frameProfiles;
              // getFrames filtre déjà les profilés incompatibles avec la gamme et les types de porte,
              // donc le fallback sur isDefault/[0] est sûr (C3 n'apparaîtra pas pour ATELIER).
              const currentFrameProfileId = this.closetDoorsService.selectedFrameProfile?.id;
              const retainedProfile = currentFrameProfileId ? frameProfiles.find(p => p.id === currentFrameProfileId) : null;
              this.closetDoorsService.selectedFrameProfile = retainedProfile ?? frameProfiles[0] ?? null;
              return this.closetDoorsService.getStructures(
                this.current.closetDoors.id,
                undefined,
                this.closetDoorsService.selectedFrameProfile?.id
              );
            })
            .then(
              (res: any) => {
                this.closetDoorsService.applyStructuresResult(res);
                // Auto-sélectionner le premier vantail et sa première section configurable
                const doors = this.current.closetDoors.doors;
                if (doors.length > 0) {
                  this.current.closetDoors.selectedDoorId = doors[0].id;
                  const firstConfigurable = (doors[0].sections ?? []).find((s: any) => !s.isFixed);
                  this.current.closetDoors.selectedSectionIndex = firstConfigurable?.index;
                }
                resolve(true);
              },
              err => reject(err)
            );
          break;
        case ClosetDoorsStandardStep.Summary:
          this.saveDoorsData().then(
            () => {
              this.closetDoorsService.getResume(this.current.closetDoors.id).then((res: any) => {
                this.current.closetDoors.resume = res;
                resolve(true);
              });
            },
            err => reject(err)
          );
          break;
        default:
          resolve(true);
          break;
      }
    });
  }
}
